import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  TermValueComponent,
  type TermValueItem,
} from '@groundworks/ngx-groundworks-ui';
import { ContactInfo, CreatedContactResponse, PaymentDetailsBilling } from '../../sdk/sdk';
import { AddContactModalComponent } from '../add-contact-modal/add-contact-modal.component';
import { formatMoneyInput, formatUsd, parseMoneyInput } from '../../utils/money';

export interface SendStatementResult {
  contactId: string;
  contactName: string;
  // null = bill the full balance due today, which is what every send did before split payments.
  amount: number | null;
}

@Component({
  selector: 'app-send-statement-modal',
  imports: [FormsModule, NgSelectComponent, ModalComponent, ModalBodyComponent, ModalFooterComponent, TermValueComponent],
  templateUrl: './send-statement-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendStatementModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly ngbModal = inject(NgbModal);

  readonly title = 'Send Customer a Statement';
  readonly contacts = signal<ContactInfo[]>([]);
  readonly opportunityId = signal<string | null>(null);

  // Supplied by ModalService from the viewer's payment-details fetch. null means the fetch failed
  // or the job is not billable: the modal cannot show a trustworthy ceiling, so it refuses to send
  // rather than falling back to a full-balance send of an amount nobody verified.
  readonly billing = signal<PaymentDetailsBilling | null>(null);

  readonly hasBillingError = computed(() => this.billing() == null);

  readonly totalsItems = computed<TermValueItem[]>(() => {
    const b = this.billing();
    if (b == null) return [];
    return [
      { term: 'Total Paid to Date', value: formatUsd(b.amountPaidToDate) },
      { term: 'Total Balance Due Today', value: formatUsd(b.amountDueNow) },
    ];
  });

  readonly selectableContacts = computed(() =>
    this.contacts()
      .filter((c) => !c.isDeleted && !!c.phone && !c.isDoNotSms)
      .map((c) => {
        const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
        return { id: c.id, name: name || c.phone || '' };
      }),
  );

  readonly selectedContactId = linkedSignal<{ id: string; name: string }[], string | null>({
    source: this.selectableContacts,
    computation: (options, previous) => {
      const prior = previous?.value ?? null;
      if (prior != null && options.some((o) => o.id === prior)) return prior;
      return options.length === 1 ? options[0].id : null;
    },
  });

  readonly splitPayment = signal(false);
  readonly splitAmountInput = signal('');

  readonly amountDueNow = computed(() => this.billing()?.amountDueNow ?? null);

  // No positive amount can be <= $0.00, so a fully-paid job (or one whose installments are not yet
  // due) has nothing to split. Sending is still allowed: a $0 statement is delivery, not a charge.
  readonly canSplit = computed(() => (this.amountDueNow() ?? 0) > 0);

  // Returns the message to display, or null when there is nothing to complain about — including
  // whenever Split Payment is unchecked, so a stale typed value can never block a full-balance send.
  readonly splitAmountError = computed<string | null>(() => {
    if (!this.splitPayment() || !this.canSplit()) return null;
    const raw = this.splitAmountInput();
    if (raw.trim() === '') return 'Enter an amount.';
    const parsed = parseMoneyInput(raw);
    if (parsed === null) return 'Enter a valid dollar amount.';
    if (parsed <= 0) return 'Amount must be greater than $0.00.';
    if (Number(parsed.toFixed(2)) !== parsed) return 'Enter at most 2 decimal places.';
    const ceiling = this.amountDueNow() ?? 0;
    if (parsed > ceiling) return `Amount cannot exceed the ${formatUsd(ceiling)} balance due today.`;
    return null;
  });

  readonly canSend = computed(() =>
    this.selectedContactId() != null && !this.hasBillingError() && this.splitAmountError() == null);

  onSplitAmountBlur(): void {
    const parsed = parseMoneyInput(this.splitAmountInput());
    // Left as typed when unparseable, so the user still sees what they entered next to the message.
    if (parsed !== null) this.splitAmountInput.set(formatMoneyInput(parsed));
  }

  readonly footerActions = computed<ModalFooterAction[]>(() => [
    { label: 'No, Go Back', type: 'secondary', action: () => this.cancel() },
    { label: 'Add New Contact', type: 'secondary', action: () => this.addContact() },
    { label: 'Send', type: 'primary', action: () => this.send(), disabled: () => !this.canSend() },
  ]);

  addContact(): void {
    const ref = this.ngbModal.open(AddContactModalComponent, { backdrop: 'static', keyboard: false, centered: true });
    ref.componentInstance.opportunityId.set(this.opportunityId());
    ref.result.then(
      (created: CreatedContactResponse) => this.applyCreatedContact(created),
      (reason) => {
        if (reason instanceof Error) console.error('Add contact modal failed', reason);
      },
    );
  }

  applyCreatedContact(created: CreatedContactResponse): void {
    const asContact = ContactInfo.fromJS({
      id: created.contactId,
      firstName: created.firstName,
      lastName: created.lastName,
      phone: created.phone,
      email: created.email,
      isPrimary: false,
      isDoNotSms: false,
      isDeleted: false,
    });
    if (!this.contacts().some((c) => c.id === asContact.id)) {
      this.contacts.set([...this.contacts(), asContact]);
    }
    this.selectedContactId.set(asContact.id);
  }

  send(): void {
    const id = this.selectedContactId();
    if (id == null || !this.canSend()) return;
    const contactName = this.selectableContacts().find((c) => c.id === id)?.name ?? '';
    const amount = this.splitPayment() && this.canSplit()
      ? parseMoneyInput(this.splitAmountInput())
      : null;
    this.activeModal.close({ contactId: id, contactName, amount } satisfies SendStatementResult);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
